import ReturnIcon from "@redotech/redo-icons/flip-backward.svg";
import ZapIconFilled from "@redotech/redo-icons/zap_filled.svg";
import { FlowType, Step as ModelStep } from "@redotech/redo-model/return-flow";
import { isExchangeAgentEnabledForTeam } from "@redotech/redo-model/return-flow/exchange-agent/exchange-agent-utils";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "@redotech/redo-model/return-flow/return-option";
import { Team } from "@redotech/redo-model/team";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";
export interface ReturnState
  extends Omit<ModelStep.Return, "type" | "options"> {
  options: (Omit<
    ModelStep.Return["options"][number],
    "method" | "adjustment"
  > & {
    type: ReturnOptionMethod["type"] | undefined;
    creditType: StoreCreditType;
    adjustmentFlat: string;
    adjustmentPercent: string;
    adjustmentType?: "flat" | "percent";
    refundPointsRedeemedForItem?: boolean;
    deductEarnedPointsFromRefund?: boolean;
    reimburseForFinalSale?: boolean;
  })[];
  exchangeAgentOverrides?: ModelStep.ExchangeAgentOverrides;
}

function round(number: number) {
  return Math.round(number * 100) / 100;
}

export function getModelReturnOptionMethod(
  type: ReturnOptionMethod["type"],
  creditType: StoreCreditType,
): ReturnOptionMethod {
  switch (type) {
    case ReturnOptionMethod.STORE_CREDIT:
      return { type: ReturnOptionMethod.STORE_CREDIT, creditType };
    case ReturnOptionMethod.EXCHANGE:
      return { type: ReturnOptionMethod.EXCHANGE };
    case ReturnOptionMethod.REFUND:
      return { type: ReturnOptionMethod.REFUND };
    case ReturnOptionMethod.REPAIR:
      return { type: ReturnOptionMethod.REPAIR };
  }
}

export const RETURN = (
  Details: React.ComponentType<StepTypeDetailsProps<ReturnState>>,
): StepType<ReturnState, ModelStep.Return> => ({
  Details: (props: StepTypeDetailsProps<ReturnState>) =>
    React.createElement(Details, props),
  customTitle(state: ReturnState) {
    return state.customTitle;
  },
  title: "Return",
  valid(state: ReturnState) {
    const exchangeAddOnValid = state.options.every((option) => {
      if (option.type === ReturnOptionMethod.EXCHANGE) {
        return (
          !option.variantExchangeAddOn ||
          (!!option.variantExchangeAddOnVariantId &&
            !!option.variantExchangeAddOnTitle &&
            !!option.variantExchangeAddOnAcceptButton &&
            !!option.variantExchangeAddOnRejectButton)
        );
      }
      return true;
    });
    const valid =
      !!state.options.length &&
      state.options.every(
        (option) =>
          !isNaN(parseFloat(option.adjustmentFlat)) &&
          !isNaN(parseFloat(option.adjustmentPercent)),
      ) &&
      exchangeAddOnValid;
    return valid;
  },
  Icon: ReturnIcon,
  iconColor: "success",
  additionalIcon(
    state: ReturnState,
    team: Team | undefined,
    flowType: FlowType,
  ) {
    const shouldRenderExchangeAgentIcon =
      flowType === FlowType.RETURN &&
      team &&
      !state.exchangeAgentOverrides?.disableExchangeAgent &&
      isExchangeAgentEnabledForTeam(
        team.settings.returnsAiExchange,
        team.settings.exchangeAgent,
        team,
      ) &&
      state.options.some(
        (option) => option.type === ReturnOptionMethod.EXCHANGE,
      );
    if (!shouldRenderExchangeAgentIcon) {
      return null;
    }
    return (
      <ZapIconFilled
        style={{
          color: "var(--redo-component-colors-utility-blue-utility-blue-500)",
        }}
      />
    );
  },
  description(state: ReturnState) {
    return state.options
      .filter(Boolean)
      .map(
        (option) =>
          ({
            [ReturnOptionMethod.STORE_CREDIT]: "Store credit",
            [ReturnOptionMethod.EXCHANGE]: "Exchange",
            [ReturnOptionMethod.REFUND]: "Refund",
            [ReturnOptionMethod.REPAIR]: "Repair",
          })[option.type!],
      )
      .join(", ");
  },
  downstream() {
    return [];
  },
  maxDownstream() {
    return 0;
  },
  empty: { options: [] },
  layout() {
    return BlockLayout.FULL;
  },
  stepDeleted(state: ReturnState) {
    return state;
  },
  toModel(state: ReturnState): ModelStep.Return {
    const exchangeAgentOverrides = buildExchangeAgentOverrides(
      state.exchangeAgentOverrides?.disableExchangeAgent,
      state.exchangeAgentOverrides?.disableVariantRecommendations,
      state.exchangeAgentOverrides?.disableProductRecommendations,
    );
    return {
      customTitle: state.customTitle,
      type: ModelStep.RETURN,
      options: state.options.map((option) => {
        const method = getModelReturnOptionMethod(
          option.type!,
          option.creditType!,
        );
        return {
          adjustment: {
            flat: +option.adjustmentFlat!,
            proportion: +option.adjustmentPercent! / 100 + 1,
          },
          labelPayer: option.labelPayer,
          method,
          ship: option.ship,
          manual_label: option.manual_label,
          exchangeTypes: option.exchangeTypes,
          chargeCustomerForNewOrderShipping:
            option.chargeCustomerForNewOrderShipping,
          variantExchangeAddOn: option.variantExchangeAddOn,
          variantExchangeAddOnVariantId: option.variantExchangeAddOnVariantId,
          variantExchangeAddOnTitle: option.variantExchangeAddOnTitle,
          variantExchangeAddOnAcceptButton:
            option.variantExchangeAddOnAcceptButton,
          variantExchangeAddOnRejectButton:
            option.variantExchangeAddOnRejectButton,
          refundPointsRedeemedForItem: option.refundPointsRedeemedForItem,
          deductEarnedPointsFromRefund: option.deductEarnedPointsFromRefund,
          reimburseForFinalSale: option.reimburseForFinalSale,
        };
      }),
      flatShipping: state.flatShipping,
      flatShippingRate: state.flatShippingRate,
      coverPackagePickup: state.coverPackagePickup,
      labelUpcharge: state.labelUpcharge,
      labelUpchargeType: state.labelUpchargeType,
      labelUpchargeValue: state.labelUpchargeValue,
      ...(exchangeAgentOverrides ? { exchangeAgentOverrides } : {}),
      locationOverride: state.locationOverride,
    };
  },
  fromModel(model: ModelStep.Return): ReturnState {
    const exchangeAgentOverrides = buildExchangeAgentOverrides(
      model.exchangeAgentOverrides?.disableExchangeAgent,
      model.exchangeAgentOverrides?.disableVariantRecommendations,
      model.exchangeAgentOverrides?.disableProductRecommendations,
    );
    return {
      customTitle: model.customTitle,
      options: model.options.map((option) => ({
        adjustmentFlat: String(option.adjustment.flat),
        adjustmentPercent: String(
          round((option.adjustment.proportion - 1) * 100),
        ),
        creditType:
          "creditType" in option.method
            ? option.method.creditType
            : StoreCreditType.GIFT_CARD,
        labelPayer: option.labelPayer,
        type: option.method.type,
        ship: option.ship,
        manual_label: option.manual_label,
        exchangeTypes: option.exchangeTypes,
        chargeCustomerForNewOrderShipping:
          option.chargeCustomerForNewOrderShipping,
        variantExchangeAddOn: option.variantExchangeAddOn,
        variantExchangeAddOnVariantId: option.variantExchangeAddOnVariantId,
        variantExchangeAddOnTitle: option.variantExchangeAddOnTitle,
        variantExchangeAddOnAcceptButton:
          option.variantExchangeAddOnAcceptButton,
        variantExchangeAddOnRejectButton:
          option.variantExchangeAddOnRejectButton,
        refundPointsRedeemedForItem: option.refundPointsRedeemedForItem,
        deductEarnedPointsFromRefund: option.deductEarnedPointsFromRefund,
        reimburseForFinalSale: option.reimburseForFinalSale,
      })),
      flatShipping: model.flatShipping,
      flatShippingRate: model.flatShippingRate,
      coverPackagePickup: model.coverPackagePickup,
      labelUpcharge: model.labelUpcharge,
      labelUpchargeType: model.labelUpchargeType,
      labelUpchargeValue: model.labelUpchargeValue,
      ...(exchangeAgentOverrides ? { exchangeAgentOverrides } : {}),
      locationOverride: model.locationOverride,
    };
  },
});

function buildExchangeAgentOverrides(
  disableExchangeAgent: boolean | undefined,
  disableVariantRecommendations: boolean | undefined,
  disableProductRecommendations: boolean | undefined,
): ModelStep.ExchangeAgentOverrides | undefined {
  const overrides: ModelStep.ExchangeAgentOverrides = {
    disableExchangeAgent,
    disableVariantRecommendations,
    disableProductRecommendations,
  };

  if (disableExchangeAgent) {
    delete overrides.disableVariantRecommendations;
    delete overrides.disableProductRecommendations;
  } else {
    delete overrides.disableExchangeAgent;
    if (!disableVariantRecommendations) {
      delete overrides.disableVariantRecommendations;
    }
    if (!disableProductRecommendations) {
      delete overrides.disableProductRecommendations;
    }
  }

  if (Object.keys(overrides).length === 0) {
    return undefined;
  }

  return overrides;
}
